import axios from "axios";
import { BussinesProfile, GalleryUpdate, UpdateProfile } from "../utils/types";
import api from "./client";
import { OnboardingPayload, ConversionPayload } from "./apiOperations";
import { store } from "../redux/store";
import { getAuth } from "firebase/auth";

// ------- User -------

export const getStripeConfig = () => api.get("/v1/config");

export const startPhoneVerificationApi = (phoneNumber: string) =>
  api.post("/v1/phone-verification/start", { phoneNumber });

export const signInWithPhoneApi = (phone: string, otpCode: string) =>
  api.post("/v1/auth/sign-in-with-phone", { phone, otpCode });

export const getUserMembershipStatus = () => api.get("v1/memberships/me");

export const getUser = (idToken: string) =>
  api.get("/v1/users/me", { headers: { Authorization: `Bearer ${idToken}` } });

export const refreshUser = () => api.get("/v1/users/me");

export const updateUserProfile = (data?: UpdateProfile, idToken?: string) =>
  api.put("/v1/users/me", data, {
    headers: { Authorization: `Bearer ${idToken}` },
  });

  export const sendWelcomeEmail = async (
    email: string,
    firstName: string,
    gymName: string,
    gymEmail: string,
    userPhone: string,
    startDate: string,
    fullName: string
  ) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const idToken = await user?.getIdToken();
  
      if (!idToken) {
        throw new Error("Authorization token missing");
      }
  
      const response = await api.post(
        "/v1/users/welcome-email",
        {
          email,
          firstName,
          gymName,
          gymEmail,
          userPhone,
          startDate,
          fullName 
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
  
      return response.data;
    } catch (error) {
      throw error;
    }
  };
  
  export const startPhoneVerification = async (phoneNumber: string) => {
    const response = await api.post("/v1/phone-verification/start", {
      phoneNumber,
    });
    return response.data;
  };
  
  export const checkPhoneVerification = async (phoneNumber: string, code: string) => {
    const response = await api.post("/v1/phone-verification/check", {
      phoneNumber,
      code,
    });
    return response.data;
  };

  export const checkDuplicates = async (phone: string, email?: string) => {
    return api.post("/v1/users/check-duplicates", email ? { phone, email } : { phone });
  };
  
// ------- Payments -------

export const cancelCurrentMembership = () => api.post("/v1/memberships/cancel");

export const pauseMembershipApi = async (days: 7 | 14 | 30) => {
  const idToken = await getAuth().currentUser?.getIdToken();
  if (!idToken) throw new Error("Authorization token missing");
  return api.post("/v1/memberships/pause", { days }, { headers: { Authorization: `Bearer ${idToken}` } });
};

export const abandonPastDueApi = async () => {
  const idToken = await getAuth().currentUser?.getIdToken();
  if (!idToken) throw new Error("Authorization token missing");
  return api.post("/v1/memberships/abandon-past-due", {}, { headers: { Authorization: `Bearer ${idToken}` } });
};

export const undoCancelCurrentMembership = () => api.post("/v1/memberships/undo-cancel");

export const cancelSubscriptionImmediately = (data: { subscriptionId: string }) =>
  api.post("/v1/subscriptions/cancel", data);

export const subscribeToProduct = async (businessProductId: string, code?: string) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    const idToken = await user?.getIdToken();

    if (!idToken) {
      throw new Error("Authorization token missing");
    }

    // Send the raw code the user entered; the backend resolves it authoritatively
    // (trial vs promo, eligibility) so it can never be silently dropped client-side.
    const payload: any = { businessProductId, source: 'web' };
    if (code) {
      payload.code = code;
    }

    // console.log("📡 Sending API request to /v1/memberships/products/basic", payload);

    const response = await api.post("/v1/memberships/products/basic", payload, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    return response.data;
  } catch (error) {
    // console.error("🚨 Error subscribing to product:", error);
    throw error;
  }
};

export const updateCheckoutSession = async (sessionId: string, stripePromoId: string) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    const idToken = await user?.getIdToken();

    if (!idToken) {
      throw new Error("Authorization token missing");
    }

    // console.log("📡 Sending API request to update checkout session", { sessionId, stripePromoId });

    const response = await api.post(
      "/v1/memberships/subscription/update",
      { sessionId, stripePromoId }, // Use stripePromoId instead of discountCode
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // console.error("🚨 Error updating checkout session:", error);
    throw error;
  }
};

export const subscribeToNextGym = (businessProductId: string) =>
  api.post("/v1/memberships/pick", {
    businessProductId: businessProductId,
  });

export const upgradeToPremium = (businessProductId: string) =>
  api.post("/v1/memberships/upgrade", {
    businessProductId: businessProductId,
  });

  export const attachPaymentMethodToSubscription = async (
    subscriptionId: string,
    paymentMethodId: string,
    idToken: string
  ) => {
    return api.post(
      `/v1/subscriptions/${subscriptionId}/payment-method`,
      { paymentMethodId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
  };  

  export const deleteMemberSettings = async (userId: string) => {
    const auth = getAuth();
    const user = auth.currentUser;
    const idToken = await user?.getIdToken();
  
    if (!idToken) {
      throw new Error("Authorization token missing");
    }
  
    return api.post(
      "/v1/member-settings/delete",
      { userId },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
  };

  export const clearNextProduct = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    const idToken = await user?.getIdToken();

    if (!idToken) throw new Error("Authorization token missing");

    return await api.delete("/v1/memberships/clear-next-product", {
      headers: { Authorization: `Bearer ${idToken}` }
    });
  };

  export const previewGymSwitch = async (newBusinessProductId: string) => {
    const auth = getAuth();
    const user = auth.currentUser;
    const idToken = await user?.getIdToken();
    if (!idToken) throw new Error("Authorization token missing");
    return await api.post("/v1/memberships/switch-current/preview", { newBusinessProductId }, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
  };

  export const switchCurrentGym = async (newBusinessProductId: string) => {
    const auth = getAuth();
    const user = auth.currentUser;
    const idToken = await user?.getIdToken();
    if (!idToken) throw new Error("Authorization token missing");
    return await api.patch("/v1/memberships/switch-current", { newBusinessProductId }, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
  };

  export const setRenewalTier = async (nextProductId: string) => {
    const auth = getAuth();
    const user = auth.currentUser;
    const idToken = await user?.getIdToken();

    if (!idToken) throw new Error("Authorization token missing");

    return await api.post("/v1/memberships/set-renewal-tier", { nextProductId }, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
  };

  export const undoNextGym = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    const idToken = await user?.getIdToken();

    if (!idToken) throw new Error("Authorization token missing");

    return await api.post("/v1/memberships/undo-next-gym", {}, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
  };
  

  export const openBillingPortal = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    const idToken = await user?.getIdToken();
  
    if (!idToken) {
      throw new Error("Authorization token missing");
    }
  
    const response = await api.post("/v1/payments/billing-portal", {}, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  
    return response.data;
  };  
  
// ------- Business (Gym Owners) -------

export const invitePartner = (tokenData: string) => {
  const params = { token: tokenData };
  return api.get(`/v1/partners/invite`, { params });
};

export const checkPartnerEmail = (email: string) =>
  api.get<{ exists: boolean }>('/v1/partners/check-email', { params: { email } });

export const getPlacesAutocomplete = (input: string) =>
  api.get<{ predictions: { description: string; placeId: string }[] }>('/v1/places/autocomplete', { params: { input } });

export const getAllBusiness = () => api.get("/v1/business");

export const getPartnerGymInfo = () => api.get("/v1/business/my-gym");

export const getPartnerGyms = () => api.get("/v1/business/my-gyms");

export const getBusinessDashboardData = (buid: string) =>
  api.get(`/v1/memberships/business/${buid}`);

export const createBusinessProfile = (data: any) =>
  api.post("/v1/business", data);

export const getBusinessProfile = (id: string) => api.get(`/v1/business/${id}`);

export const getBusinessGallery = () =>
  api.get("/v1/business/gallery/signed-url");

export const updateBusinessGallery = (data: any) =>
  api.put("/v1/business/gallery", data);

export const updateMembershipOnboarding = (membershipId: string, payload: OnboardingPayload) =>
  api.put(`/v1/memberships/${membershipId}/onboarding`, payload);

export const updateMembershipConversion = (membershipId: string, payload: ConversionPayload) =>
  api.put(`/v1/memberships/${membershipId}/conversion`, payload);

export const fetchBusinessOwnerEmailByName = async (gymName: string): Promise<string> => {
  const auth = getAuth();
  const user = auth.currentUser;
  const idToken = await user?.getIdToken();

  const response = await api.get(`/v1/business/lookup-owner-email`, {
    params: { name: gymName },
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  return response.data.email;
};
  
// ---- Upload Image --> Step 1 ---------

export const uploadImage = (file: any) => {
  const result = api.post("/v1/business/gallery/upload", file, {
    headers: {
      ContentType: "multipart/form-data",
    },
  });
  return result;
};

// ---- Upload Image --> Step 2 ---------
export const updateGallery = (data: GalleryUpdate[]) =>
  api.put("/v1/business/gallery", data);

// ------- Discount Codes -------

// Validate a discount code
export const validateDiscountCode = (code: string, subscriptionId?: string, businessProductId?: string) =>
  api.post("/v1/discount-codes/validate", { code, subscriptionId, businessProductId });

// Record usage of a discount code after successful payment
export const recordDiscountCodeUsage = (data: { code: string; subscriptionId: string, email: string }) =>
  api.post("/v1/discount-codes/record-usage", data);

// ------- Referral Codes -------

export const getUserReferralCode = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  const idToken = await user?.getIdToken();

  if (!idToken) {
    throw new Error("Authorization token missing");
  }

  return api.get("/v1/users/me/referral-code", {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });
};

// ------- Waitlist -------

export const submitTampaWaitlistEntry = async (entry: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  favoriteGym?: string;
}) => {
  const response = await api.post(
    "/v1/waitlist/tampa",
    entry
  );

  return response.data;
};

export const submitOrlandoWaitlistEntry = async (entry: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  favoriteGym?: string;
}) => {
  const response = await api.post(
    "/v1/waitlist/orlando",
    entry
  );

  return response.data;
};

export const submitNewYorkWaitlistEntry = async (entry: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  favoriteGym?: string;
}) => {
  const response = await api.post(
    "/v1/waitlist/newyork",
    entry
  );

  return response.data;
};

// ------- Partner Leads -------

export const submitPartnerLead = async (entry: {
  name: string;
  businessName: string;
  email: string;
  phone: string;
  zipCode: string;
  locationCount: string;
  website?: string;
  source: string;
}) => {
  const response = await api.post("/v1/leads/partner", entry);
  return response.data;
};

// ------- Admin -------

export const adminGetUsers = (params?: { search?: string; gym?: string; status?: string }) =>
  api.get("/v1/admin/users", { params });

export const adminGetUserHistory = (userId: string) =>
  api.get(`/v1/admin/users/${userId}/memberships`);

export type AdminMoveRequest = {
  mode: "current" | "next";
  businessId: string;
  plan: "auto" | "basic" | "premium" | "premium_quarterly" | "premium_yearly";
  settlement?: "none" | "charge" | "credit" | "refund";
  acknowledgeCheckIn?: boolean;
};

export const adminGetUserBilling = (userId: string) => api.get(`/v1/admin/users/${userId}/billing`);

export const adminPreviewMove = (userId: string, data: AdminMoveRequest) =>
  api.post(`/v1/admin/users/${userId}/move/preview`, data);

export const adminMoveMember = (userId: string, data: AdminMoveRequest) =>
  api.post(`/v1/admin/users/${userId}/move`, data);

export const adminPauseMember = (userId: string, days: 7 | 14 | 30) =>
  api.post(`/v1/admin/users/${userId}/pause`, { days });

export const adminUndoPause = (userId: string) => api.post(`/v1/admin/users/${userId}/undo-pause`);

export const adminResumeMember = (userId: string) => api.post(`/v1/admin/users/${userId}/resume`);

export const adminDeleteUser = (userId: string, data: { reason: string; confirmEmail: string }) =>
  api.delete(`/v1/admin/users/${userId}`, { data });

export const adminUpdateUser = (userId: string, data: { firstName?: string; lastName?: string; phone?: string }) =>
  api.patch(`/v1/admin/users/${userId}`, data);

export const adminChangeUserEmail = (userId: string, newEmail: string) =>
  api.post(`/v1/admin/users/${userId}/change-email`, { newEmail });

export const adminDeleteMembership = (membershipId: string) =>
  api.delete(`/v1/admin/memberships/${membershipId}`);

export const adminGetDiscountCodes = () =>
  api.get("/v1/admin/discount-codes");

export const adminCreateDiscountCode = (data: {
  code: string;
  type: "fixed" | "percentage" | "trial";
  amount: number;
  description: string;
  maxUses: number;
  expiresAt: string;
  isActive: boolean;
  firstTimeOnly: boolean;
  trialDays?: number;
  scopeCities?: string[] | null;
  scopePrices?: number[] | null;
}) => api.post("/v1/admin/discount-codes", data);

export const adminUpdateDiscountCode = (id: string, data: {
  isActive: boolean;
}) => api.patch(`/v1/admin/discount-codes/${id}`, data);

export const adminDeleteDiscountCode = (id: string) =>
  api.delete(`/v1/admin/discount-codes/${id}`);

interface SocialEventInput {
  title: string;
  photoUrl?: string | null;
  eventDate: string;
  eventTime: string;
  city: string;
  state: string;
  locationName: string;
  price: string;
  rsvpLink?: string | null;
  isFreeForMembers: boolean;
  isPublished: boolean;
  // Class check-in linking (all optional): host studio + a class from its schedule.
  hostBusinessId?: string | null;
  externalClassId?: string | null;
  externalLocationId?: string | null;
  classMeta?: Record<string, any> | null;
  className?: string | null;
  classStartTime?: string | null;
}

// Search host businesses that have an active booking integration (returns provider).
export const adminSearchHostBusinesses = (q: string) =>
  api.get(`/v1/admin/social-events/host-search`, { params: { q } });

// List a host business's classes from its partner schedule, for the class picker.
export const adminGetProviderClasses = (
  provider: string,
  businessId: string,
  start?: string,
  end?: string,
  locationId?: string,
) => api.get(`/integrations/${provider}/classes`, { params: { businessId, start, end, locationId: locationId || undefined } });

export const adminUploadImage = (file: File) => {
  const fd = new FormData();
  fd.append("image", file);
  return api.post("/v1/admin/upload-image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const adminGetSocialEvents = () => api.get("/v1/admin/social-events");

export const adminCreateSocialEvent = (data: SocialEventInput) =>
  api.post("/v1/admin/social-events", data);

export const adminUpdateSocialEvent = (id: string, data: Partial<SocialEventInput>) =>
  api.put(`/v1/admin/social-events/${id}`, data);

export const adminDeleteSocialEvent = (id: string) =>
  api.delete(`/v1/admin/social-events/${id}`);

export const adminSendEmail = (data: {
  subject: string;
  message: string;
  filter: {
    type: "all" | "gym" | "product" | "userIds";
    gymName?: string;
    productName?: string;
    userIds?: string[];
  };
}) => api.post("/v1/admin/notifications/email", data);

export const adminSendPush = (data: {
  title: string;
  body: string;
  filter: {
    type: "all" | "gym" | "product" | "userIds";
    gymName?: string;
    productName?: string;
    userIds?: string[];
  };
}) => api.post("/v1/notifications/push", data);

export const adminInvitePartner = (email: string) =>
  api.post("/v1/partners/invite", { email });

export const adminGetAllBusiness = () => api.get("/v1/admin/business");

export const adminUpdateBusinessVisibility = (businessId: string, hide: boolean) =>
  api.patch(`/v1/admin/business/${businessId}/visibility`, { hide });

export const adminUpsertIntegration = (
  businessId: string,
  data: {
    provider: "mindbody" | "marianatek" | "other";
    externalBusinessId?: string;
    externalLocationId?: string;
    apiBaseUrl?: string;
    jrBookingServiceId?: string;
    isActive: boolean;
  }
) => api.put(`/v1/admin/business/${businessId}/integrations`, data);

export const adminGetIntegrations = (businessId: string) =>
  api.get(`/v1/admin/business/${businessId}/integrations`);

export const adminGetOwners = (businessId: string) =>
  api.get(`/v1/admin/business/${businessId}/owners`);

export const adminAddOwner = (businessId: string, email: string) =>
  api.post(`/v1/admin/business/${businessId}/owners`, { email });

export const adminRemoveOwner = (businessId: string, userId: string) =>
  api.delete(`/v1/admin/business/${businessId}/owners/${userId}`);

export const adminGrantStudioAccess = (data: {
  email: string;
  permissionRole: "owner" | "manager" | "frontDesk";
  businessIds: string[];
}) => api.post("/v1/admin/owners/bulk", data);

export const adminGetPayoutReport = (start: string, end: string) =>
  api.get("/v1/admin/reports/payouts", { params: { start, end } });

export const adminGetBookingsReport = (params: {
  start: string;
  end: string;
  businessIds?: string;
  basis: "booked" | "class";
}) => api.get("/v1/admin/reports/bookings", { params });

export const adminGetUsageReport = (params: { start: string; end: string }) =>
  api.get("/v1/admin/reports/usage", { params });

export const adminGetReferralsReport = (start: string, end: string) =>
  api.get("/v1/admin/reports/referrals", { params: { start, end } });

export const adminGetDropConversion = (params: {
  start: string;
  end: string;
  businessIds?: string;
  basis: "booked" | "class";
}) => api.get("/v1/admin/reports/bookings/drop-conversion", { params });

export const adminGetBookingsList = (params: {
  start: string;
  end: string;
  businessIds?: string;
  basis: "booked" | "class";
}) => api.get("/v1/admin/reports/bookings/list", { params });

export const uploadLogoImage = (file: File) => {
  const form = new FormData();
  form.append("image", file);
  return api.post("/v1/business/logo/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const submitBusinessIntegration = (data: {
  provider: "mindbody" | "marianatek" | "other";
  externalBusinessId?: string;
  externalLocationId?: string;
  apiBaseUrl?: string;
  jrBookingServiceId?: string;
}) => api.post("/v1/business/integration", data);

export const getMbActivationCode = (data: { inviteToken: string; siteId: string }) =>
  api.post<{ activationCode: string }>("/v1/partner/mindbody/activation-code", data);

export const onboardPartner = (data: {
  inviteToken: string;
  ownerEmail: string;
  ownerPassword?: string;
  coOwners?: { email: string; password?: string }[];
  name: string;
  description: string;
  address: { fullAddress: string };
  amenities: string[];
  trainingStyles: string[];
  socialLinks: { name: string; socialLink: string }[];
  basicProductPrice: number;
  premiumProductPrice: number;
  premiumQuarterlyProductPrice?: number;
  premiumYearlyProductPrice?: number;
  taxRateBps?: number;
  hasClassBookings: boolean;
  integrationProvider?: string;
  siteId?: string;
}) => api.post("/v1/partner/onboard", data);

export const getClassDropSettingsApi = (businessId: string) =>
  api.get("/class-drops/settings", { params: { businessId } });

export const updateClassDropSettingsApi = (data: {
  businessId: string;
  enabled: boolean;
  mode: string;
  maxJrSpotsPerClass: number;
  releaseWindowHours: number;
  minSpotsRemaining: number;
  firstTimeMode: string;
  lastMinuteFillEnabled: boolean;
  lastMinuteFillHours: number;
  lastMinuteFillKeepReserved: number;
}) => api.put("/class-drops/settings", data);

export const applyClassDropPresetApi = (
  businessId: string,
  mode: string,
  enabled?: boolean
) => api.post("/class-drops/settings/preset", { businessId, mode, enabled });

export const getClassDropEligibleClassesApi = (businessId: string) =>
  api.get("/class-drops/eligible-classes", { params: { businessId } });

export const setClassDropEligibleClassApi = (data: {
  businessId: string;
  provider: string;
  externalClassTypeId: string;
  className?: string;
  eligible: boolean;
}) => api.post("/class-drops/eligible-classes", data);

export const getClassDropClassTypesApi = (businessId: string, provider?: string) =>
  api.get("/class-drops/class-types", { params: { businessId, provider } });

export const getClassDropMetricsApi = (businessId: string, since?: string, until?: string) =>
  api.get("/class-drops/metrics", { params: { businessId, since, until } });

export const getPartnerStaffApi = (businessId: string) =>
  api.get(`/v1/partner/business/${businessId}/staff`);

export const addPartnerStaffApi = (businessId: string, email: string, permissionRole: string) =>
  api.post(`/v1/partner/business/${businessId}/staff`, { email, permissionRole });

export const updatePartnerStaffRoleApi = (businessId: string, userId: string, permissionRole: string) =>
  api.patch(`/v1/partner/business/${businessId}/staff/${userId}`, { permissionRole });

export const removePartnerStaffApi = (businessId: string, userId: string) =>
  api.delete(`/v1/partner/business/${businessId}/staff/${userId}`);

export const revokePartnerInviteApi = (businessId: string, inviteId: string) =>
  api.delete(`/v1/partner/business/${businessId}/invites/${inviteId}`);

export const getPartnerProjectionApi = (businessId: string) =>
  api.get(`/v1/partner/business/${businessId}/projection`);

export const getPartnerReportApi = (businessId: string, start?: string, end?: string) =>
  api.get(`/v1/partner/business/${businessId}/report`, { params: { start, end } });

export type AdminAudience = {
  membership?: Array<"active" | "cancelling" | "paused" | "lapsed" | "never">;
  near?: { label?: string; latitude: number; longitude: number; radiusMiles: number };
  gymName?: string;
  productName?: "basic" | "premium";
  abandonedCart?: { days: number; gymId?: string };
  userIds?: string[];
};

export const adminPreviewAudience = (data: {
  audience: AdminAudience;
  channel: "push" | "email";
  capHours: number;
  overrideCap: boolean;
}) => api.post("/v1/admin/audience/preview", data);

export const adminSendToAudience = (data: {
  audience: AdminAudience;
  channel: "push" | "email";
  capHours: number;
  overrideCap: boolean;
  title: string;
  body: string;
  test?: boolean;
}) => api.post("/v1/admin/audience/send", data);

export const getMetros = () => api.get("/v1/businesses/metros");
